import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { topicService } from '../../../services';
import { deckService } from '../../../services/deck.service.js';
import Loader from '../../common/Loader/Loader';
import Toast from '../../common/Toast/Toast';
import Dropdown from '../../common/Dropdown/Dropdown';
import { Search, User as UserIcon, Clock, FileText, Pencil, Trash2, X, ChevronDown, Calendar, ArchiveRestore, Image as ImageIcon, Upload, Video, Music, Play, Pause } from 'lucide-react';

// Deck Card Media Carousel Component
const DeckCardMedia = ({ assets, coverType }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

  // Get valid assets (filter out null/undefined)
  const validAssets = assets.filter(a => a !== null && a !== undefined);

  // Auto-slide for multiple images
  useEffect(() => {
    if (validAssets.length > 1 && !isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % validAssets.length);
      }, 1500);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [validAssets.length, isPaused]);

  // Reset index when assets change
  useEffect(() => {
    setCurrentIndex(0);
  }, [assets]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    // Pause briefly when manually changing slide
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  const currentAsset = validAssets[currentIndex];

  // Show no preview placeholder if no assets
  if (!currentAsset || coverType === 'none' || !coverType) {
    return (
      <div className="deck-card-media deck-card-media--no-preview">
        <div className="deck-card-no-preview">
          <ImageIcon size={24} />
          <span className="deck-card-no-preview-text">None</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="deck-card-media"
      ref={carouselRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="deck-card-media-content">
        {coverType === 'image' && currentAsset.type === 'IMAGE' && (
          <img src={currentAsset.source_url} alt="Deck cover" className="deck-card-media-image" />
        )}
        {coverType === 'video' && currentAsset.type === 'VIDEO' && (
          <div className="deck-card-media-video">
            <Video size={20} />
          </div>
        )}
        {coverType === 'audio' && currentAsset.type === 'AUDIO' && (
          <div className="deck-card-media-audio">
            <Music size={20} />
          </div>
        )}

        {/* Carousel indicators for multiple images */}
        {validAssets.length > 1 && (
          <div className="deck-card-carousel-dots">
            {validAssets.map((_, index) => (
              <button
                key={index}
                className={`deck-card-carousel-dot ${index === currentIndex ? 'deck-card-carousel-dot--active' : ''}`}
                onClick={() => handleDotClick(index)}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Pause/Play indicator for carousel */}
        {validAssets.length > 1 && (
          <div className="deck-card-carousel-indicator">
            {isPaused ? <Pause size={10} /> : <Play size={10} />}
          </div>
        )}
      </div>

      {/* Asset text overlay */}
      {currentAsset.text && (
        <div className="deck-card-media-text">
          {currentAsset.text}
        </div>
      )}
    </div>
  );
};
import './DeckManager.css';
import { DEFAULT_LANGUAGE } from '@shared/languages.js';

const DeckManager = ({ layout = 'stacked', fullWidth = false }) => {
  const navigate = useNavigate();
  const [deckSearch, setDeckSearch] = useState('');
  const [deckName, setDeckName] = useState('');
  const [deckTopic, setDeckTopic] = useState('');
  const [deckIdentityKey, setDeckIdentityKey] = useState('');
  const [deckIdentityId, setDeckIdentityId] = useState('');
  const [deckDurationFrom, setDeckDurationFrom] = useState('');
  const [deckDurationTo, setDeckDurationTo] = useState('');
  const [deckPriority, setDeckPriority] = useState('');
  const [deckPosition, setDeckPosition] = useState('');
  const [coverType, setCoverType] = useState('none');
  const [deckLangCode, setDeckLangCode] = useState('en');
  const [assets, setAssets] = useState([]);
  const [deckError, setDeckError] = useState(null);

  // Field error states
  const [deckNameError, setDeckNameError] = useState('');
  const [deckTopicError, setDeckTopicError] = useState('');
  const [deckDurationError, setDeckDurationError] = useState('');
  const [deckPriorityError, setDeckPriorityError] = useState('');
  const [deckPositionError, setDeckPositionError] = useState('');
  const [assetsError, setAssetsError] = useState('');

  // Topics state
  const [topics, setTopics] = useState([]);
  const [topicLoading, setTopicLoading] = useState(false);

  // Languages state
  const [languages, setLanguages] = useState([]);
  const [languageLoading, setLanguageLoading] = useState(false);

  // Deck states
  const [decks, setDecks] = useState([]);
  const [deckLoading, setDeckLoading] = useState(false);
  const [creatingDeck, setCreatingDeck] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [deckSearchLoading, setDeckSearchLoading] = useState(false);
  const [deckPage, setDeckPage] = useState(1);
  const [deckTotalPages, setDeckTotalPages] = useState(1);
  const [deckHasMore, setDeckHasMore] = useState(true);
  const [deckStatusFilter, setDeckStatusFilter] = useState('all');
  const deckListRef = useRef(null);
  const deckFetchInProgressRef = useRef(false);
  const deckPageRef = useRef(1);
  const coverInputRef = useRef(null);
  const deckFormRef = useRef(null);

  // Status filter options
  const statusFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'created', label: 'Created' },
    { value: 'modified', label: 'Modified' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
    { value: 'deleted', label: 'Deleted' },
  ];

  // Deck edit/delete states
  const [editingDeck, setEditingDeck] = useState(null);
  const [editingDeckName, setEditingDeckName] = useState('');
  const [editingDeckTopic, setEditingDeckTopic] = useState('');
  const [editingDeckIdentityKey, setEditingDeckIdentityKey] = useState('');
  const [editingDeckIdentityId, setEditingDeckIdentityId] = useState('');
  const [editingDeckDurationFrom, setEditingDeckDurationFrom] = useState('');
  const [editingDeckDurationTo, setEditingDeckDurationTo] = useState('');
  const [deletingDeckId, setDeletingDeckId] = useState(null);
  const [archivingDeckId, setArchivingDeckId] = useState(null);

  // Upload states
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Toast state
  const [toast, setToast] = useState(null);

  // Fetch topics on mount (only once)
  useEffect(() => {
    fetchTopics();
    fetchLanguages();
  }, []); // Run only once on mount

  const fetchTopics = async () => {
    setTopicLoading(true);
    try {
      const response = await topicService.getAllTopics();
      if (response.success) {
        setTopics(response.data.topics || []);
      }
    } catch (err) {
      console.error('Fetch topics error:', err);
    } finally {
      setTopicLoading(false);
    }
  };

  const fetchLanguages = async () => {
    setLanguageLoading(true);
    try {
      const response = await deckService.getLanguages();
      if (response.success) {
        setLanguages(response.data.langCodes || []);
      }
    } catch (err) {
      console.error('Fetch languages error:', err);
    } finally {
      setLanguageLoading(false);
    }
  };

  // Deck click handler - navigate to deck details
  const handleDeckClick = async (e, deckId, deckStatus) => {
    // Don't navigate if any deck is being edited, deleting or archiving
    if (editingDeck?._id || deletingDeckId || archivingDeckId) {
      return;
    }
    // Check if modifier keys are pressed (Ctrl, Cmd, Shift, Alt)
    const isModifierPressed = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

    if (isModifierPressed) {
      // Open in new tab if modifier key is pressed
      window.open(`/dashboard/decks/${deckId}`, '_blank');
    } else {
      // Navigate to deck detail page with state to track where we came from (current window)
      navigate(`/dashboard/decks/${deckId}`, { state: { from: 'deck-list' } });
    }
  };

  // Cover type change handler
  const handleCoverTypeChange = (e) => {
    const newType = e.target.value;
    setCoverType(newType);
    setAssets([]); // Clear assets when cover type changes
    setUploadingSlot(null);
    setUploadProgress(0);
  };

  // Asset upload handler using signed URL
  const handleAssetUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const file = files[0]; // Only take the first file (one-by-one upload)

    // Find the slot index for uploading and set loading state immediately
    let targetSlot = null;
    if (coverType === 'image') {
      // Find first empty slot (index where asset is null/undefined or beyond array length)
      targetSlot = assets.findIndex(a => a === null || a === undefined);
      // If no empty slot found (all 3 filled or array shorter), use next available slot
      if (targetSlot === -1) {
        targetSlot = assets.length;
      }
      // Ensure max 3 images
      if (targetSlot >= 3) {
        setDeckError('Maximum 3 images allowed');
        if (coverInputRef.current) coverInputRef.current.value = '';
        return;
      }
    } else {
      targetSlot = 'media';
    }

    // Set uploading state BEFORE any validation
    setUploadingSlot(targetSlot);
    setUploadProgress(0);

    setDeckError(null);

    // Validate file type based on cover type
    if (coverType === 'image' && !file.type.startsWith('image/')) {
      setDeckError('Please select image files only');
      setUploadingSlot(null);
      if (coverInputRef.current) coverInputRef.current.value = '';
      return;
    }
    if (coverType === 'video' && !file.type.startsWith('video/')) {
      setDeckError('Please select video files only');
      setUploadingSlot(null);
      if (coverInputRef.current) coverInputRef.current.value = '';
      return;
    }
    if (coverType === 'audio' && !file.type.startsWith('audio/')) {
      setDeckError('Please select audio files only');
      setUploadingSlot(null);
      if (coverInputRef.current) coverInputRef.current.value = '';
      return;
    }

    // For images, enforce maximum 3 images (count only non-null assets)
    if (coverType === 'image' && assets.filter(a => a !== null && a !== undefined).length >= 3) {
      setDeckError('Maximum 3 images allowed');
      setUploadingSlot(null);
      if (coverInputRef.current) coverInputRef.current.value = '';
      return;
    }

    try {
      // Use setTimeout to ensure UI updates before starting upload
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get signed URL from backend
      const signedUrlResponse = await deckService.getUploadSignedUrl(file.name, file.type, coverType);
      if (!signedUrlResponse.success) {
        throw new Error('Failed to get upload URL');
      }

      const { signedUrl, publicUrl } = signedUrlResponse.data;

      // Upload file directly to GCS using signed URL with progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl);

        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error('Failed to upload file'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Add asset with public URL
      const asset = {
        source_url: publicUrl,
        text: file.name,
        type: coverType === 'image' ? 'IMAGE' : coverType === 'video' ? 'VIDEO' : 'AUDIO'
      };

      // For images, place in the correct slot (0, 1, or 2)
      if (coverType === 'image') {
        setAssets(prev => {
          // Find the first empty slot
          let insertIndex = prev.findIndex(a => a === null || a === undefined);
          if (insertIndex === -1) {
            // All slots filled, this shouldn't happen due to the check above
            return [...prev, asset];
          }
          const newAssets = [...prev];
          newAssets[insertIndex] = asset;
          return newAssets;
        });
      } else {
        // For video/audio, replace existing or add new
        setAssets(prev => prev.length > 0 ? [asset] : [...prev, asset]);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setDeckError(`Failed to upload ${file.name}: ${err.message}`);
    } finally {
      // Clear uploading state
      setUploadingSlot(null);
      setUploadProgress(0);
    }

    // Clear input
    if (coverInputRef.current) {
      coverInputRef.current.value = '';
    }
  };

  // Remove asset handler
  const handleRemoveAsset = (index) => {
    setAssets(prev => {
      if (coverType === 'image') {
        // For images, set the slot to null to maintain slot positions
        const newAssets = [...prev];
        newAssets[index] = null;
        return newAssets;
      } else {
        // For video/audio, remove the asset
        return prev.filter((_, i) => i !== index);
      }
    });
  };

  // Update asset text handler
  const handleAssetTextChange = (index, text) => {
    setAssets(prev => {
      const newAssets = [...prev];
      // Only update text if the asset exists
      if (newAssets[index]) {
        newAssets[index].text = text;
      }
      return newAssets;
    });
  };

  const fetchDecks = useCallback(async (page = 1, append = false) => {
    // Prevent duplicate fetches
    if (deckFetchInProgressRef.current) {
      return;
    }
    deckFetchInProgressRef.current = true;
    // Update ref immediately to prevent concurrent fetches
    deckPageRef.current = page;

    if (!append) {
      setDeckLoading(true);
    }
    setDeckError(null);
    try {
      const params = { page, limit: 5 };
      if (deckStatusFilter !== 'all') {
        params.status = deckStatusFilter;
      }
      const response = await deckService.getAllDecks(params);
      if (response?.success) {
        const newDecks = response.data || [];
        const totalPages = response.pagination?.totalPages || 1;
        setDeckTotalPages(totalPages);
        setDeckHasMore(page < totalPages);
        if (append) {
          setDecks(prev => {
            // Filter out any duplicates by ID
            const existingIds = new Set(prev.map(d => d._id));
            const filteredNewDecks = newDecks.filter(d => !existingIds.has(d._id));
            return [...prev, ...filteredNewDecks];
          });
        } else {
          setDecks(newDecks);
        }
        setDeckPage(page);
      } else {
        if (!append) setDecks([]);
      }
    } catch (err) {
      console.error('Fetch decks error:', err);
      if (!append) {
        setDeckError('Failed to load decks');
        setDecks([]);
      }
    } finally {
      setDeckLoading(false);
      deckFetchInProgressRef.current = false;
    }
  }, [deckStatusFilter]);

  // Debounced search for decks - also handles initial load and status changes
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (deckSearch.trim()) {
        setDeckSearchLoading(true);
        setDeckHasMore(false);
        setDecks([]); // Clear decks to show loader
        try {
          const params = { page: 1, limit: 5 };
          if (deckStatusFilter !== 'all') {
            params.status = deckStatusFilter;
          }
          const response = await deckService.searchDecks(deckSearch.trim(), params);
          if (response?.success) {
            setDecks(response.data || []);
            setDeckTotalPages(response.pagination?.totalPages || 1);
            setDeckHasMore(1 < (response.pagination?.totalPages || 1));
            setDeckPage(1);
          } else {
            setDecks([]);
            setDeckHasMore(false);
          }
        } catch (err) {
          console.error('Deck search error:', err);
          setDecks([]);
          setDeckHasMore(false);
        } finally {
          setDeckSearchLoading(false);
        }
      } else {
        // Fetch all decks when search is cleared
        setDecks([]); // Clear decks to show loader
        await fetchDecks(1, false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [deckSearch, deckStatusFilter, fetchDecks]);

  // Keep deckPageRef in sync with deckPage state
  useEffect(() => {
    deckPageRef.current = deckPage;
  }, [deckPage]);

  // Scroll event listener for infinite scroll
  useEffect(() => {
    const container = deckListRef.current;

    const handleScroll = () => {
      if (!container || !deckHasMore || deckFetchInProgressRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const threshold = 100; // Load more when 100px from bottom

      // Use ref value instead of state to avoid closure issues
      if (scrollTop + clientHeight >= scrollHeight - threshold && !deckLoading && deckPageRef.current < deckTotalPages) {
        const nextPage = deckPageRef.current + 1;
        fetchDecks(nextPage, true);
      }
    };

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [deckLoading, deckHasMore, deckTotalPages]);

  const handleCreateDeck = async () => {
    // Clear previous errors
    setDeckNameError('');
    setDeckTopicError('');
    setDeckDurationError('');
    setDeckPriorityError('');
    setDeckPositionError('');
    setAssetsError('');

    if (!deckName.trim()) {
      setDeckNameError('Please enter a deck name');
      return;
    }

    if (!deckTopic.trim()) {
      setDeckTopicError('Please select a category/topic');
      return;
    }

    if (!deckDurationFrom || !deckDurationTo) {
      setDeckDurationError('Please select From and To dates');
      return;
    }

    // Validate dates - To date cannot be before From date
    if (deckDurationFrom && deckDurationTo) {
      const fromDate = new Date(deckDurationFrom);
      const toDate = new Date(deckDurationTo);
      if (toDate < fromDate) {
        setDeckDurationError('To date/time cannot be before From date/time');
        return;
      }
    }

    // Validate priority - numeric only, no decimals
    if (deckPriority && (!/^\d+$/.test(deckPriority))) {
      setDeckPriorityError('Priority must be a whole number');
      return;
    }

    // Validate position - numeric only, no decimals
    if (deckPosition && (!/^\d+$/.test(deckPosition))) {
      setDeckPositionError('Position must be a whole number');
      return;
    }

    // Filter out null assets and validate
    const validAssets = assets.filter(a => a !== null && a !== undefined);

    // Validate assets based on cover type
    if (coverType !== 'none' && validAssets.length === 0) {
      setAssetsError('Please upload at least one file');
      return;
    }

    setCreatingDeck(true);
    setDeckError(null);

    try {
      const response = await deckService.createDeck(
        deckName.trim(),
        deckTopic.trim(),
        deckDurationFrom,
        deckDurationTo,
        (deckIdentityKey || '').trim(),
        (deckIdentityId || '').trim(),
        validAssets,
        deckPriority ? parseInt(deckPriority) : null,
        deckPosition ? parseInt(deckPosition) : null,
        coverType,
        deckLangCode
      );

      if (response.success) {
        setDeckName('');
        setDeckTopic('');
        setDeckIdentityKey('');
        setDeckIdentityId('');
        setDeckDurationFrom('');
        setDeckDurationTo('');
        setDeckPriority('');
        setDeckPosition('');
        setCoverType('none');
        setDeckLangCode('en');
        setAssets([]);
        setToast({ message: 'Deck created successfully', type: 'success' });
        // Refresh deck list
        setDeckPage(1);
        if (deckSearch.trim()) {
          const searchParams = { page: 1, limit: 5 };
          if (deckStatusFilter !== 'all') {
            searchParams.status = deckStatusFilter;
          }
          const searchResponse = await deckService.searchDecks(deckSearch.trim(), searchParams);
          if (searchResponse?.success) {
            setDecks(searchResponse.data?.decks || []);
            setDeckTotalPages(searchResponse.data.totalPages || 1);
            setDeckHasMore(1 < (searchResponse.data.totalPages || 1));
          }
        } else {
          await fetchDecks(1, false);
        }
        // Select the newly created deck
        if (response?.data?.deck?._id) {
          setSelectedDeck(response.data.deck._id);
        }
      } else {
        setToast({ message: response.message || 'Failed to create deck', type: 'error' });
      }
    } catch (err) {
      console.error('Create deck error:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.response?.message || err.message || 'Failed to create deck';
      setDeckError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setCreatingDeck(false);
    }
  };

  const handleUpdateDeck = async () => {
    if (!editingDeck) return;

    // Clear previous errors
    setDeckNameError('');
    setDeckTopicError('');
    setDeckDurationError('');
    setDeckPriorityError('');
    setDeckPositionError('');
    setAssetsError('');

    if (!deckName.trim()) {
      setDeckNameError('Please enter a deck name');
      return;
    }

    if (!deckTopic.trim()) {
      setDeckTopicError('Please select a category/topic');
      return;
    }

    if (!deckDurationFrom || !deckDurationTo) {
      setDeckDurationError('Please select From and To dates');
      return;
    }

    // Validate dates - To date cannot be before From date
    if (deckDurationFrom && deckDurationTo) {
      const fromDate = new Date(deckDurationFrom);
      const toDate = new Date(deckDurationTo);
      if (toDate < fromDate) {
        setDeckDurationError('To date/time cannot be before From date/time');
        return;
      }
    }

    // Validate priority - numeric only, no decimals
    if (deckPriority && (!/^\d+$/.test(deckPriority))) {
      setDeckPriorityError('Priority must be a whole number');
      return;
    }

    // Validate position - numeric only, no decimals
    if (deckPosition && (!/^\d+$/.test(deckPosition))) {
      setDeckError('Position must be a whole number');
      return;
    }

    // Filter out null assets and validate
    const validAssets = assets.filter(a => a !== null && a !== undefined);

    // Validate assets based on cover type
    if (coverType !== 'none' && validAssets.length === 0) {
      setAssetsError('Please upload at least one file');
      return;
    }

    setCreatingDeck(true);
    setDeckError(null);

    try {
      const response = await deckService.updateDeck(
        editingDeck._id,
        deckName.trim(),
        deckTopic.trim(),
        deckDurationFrom,
        deckDurationTo,
        (deckIdentityKey || '').trim(),
        (deckIdentityId || '').trim(),
        validAssets,
        deckPriority ? parseInt(deckPriority) : null,
        deckPosition ? parseInt(deckPosition) : null,
        coverType,
        deckLangCode
      );

      if (response.success) {
        setToast({ message: 'Deck updated successfully', type: 'success' });
        // Reset form
        handleCancelEditDeck();
        // Refresh deck list
        setDeckPage(1);
        if (deckSearch.trim()) {
          const searchParams = { page: 1, limit: 5 };
          if (deckStatusFilter !== 'all') {
            searchParams.status = deckStatusFilter;
          }
          const searchResponse = await deckService.searchDecks(deckSearch.trim(), searchParams);
          if (searchResponse?.success) {
            setDecks(searchResponse.data?.decks || []);
            setDeckTotalPages(searchResponse.data.totalPages || 1);
            setDeckHasMore(1 < (searchResponse.data.totalPages || 1));
          }
        } else {
          await fetchDecks(1, false);
        }
      } else {
        setToast({ message: response.message || 'Failed to update deck', type: 'error' });
      }
    } catch (err) {
      console.error('Update deck error:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.response?.message || err.message || 'Failed to update deck';
      setDeckError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setCreatingDeck(false);
    }
  };

  const handleDeckSelect = (deckId) => {
    setSelectedDeck(deckId);
  };

  // Start editing deck - populate left form
  const handleStartEditDeck = (deck, e) => {
    e.stopPropagation();
    // Prevent editing for archived or deleted decks
    if (deck.status === 'archived' || deck.status === 'deleted') {
      return;
    }
    // Populate the left form with deck data
    setEditingDeck(deck);
    setDeckName(deck.name || '');

    // Find matching topic to get identityKey and identityId
    const matchingTopic = topics.find(t => t.name === deck.topic);
    const identityKey = deck.identityKey || (matchingTopic ? matchingTopic.identityKey : '');
    const identityId = deck.identityId || (matchingTopic ? matchingTopic.id : '');

    setDeckTopic(deck.topic || '');
    setDeckIdentityKey(identityKey || '');
    setDeckIdentityId(identityId || '');
    setDeckDurationFrom(deck.durationFrom ? deck.durationFrom.slice(0, 16) : '');
    setDeckDurationTo(deck.durationTo ? deck.durationTo.slice(0, 16) : '');
    setDeckPriority(deck.priority || '');
    setDeckPosition(deck.position || '');
    setCoverType(deck.coverType || 'none');
    setDeckLangCode(deck.langCode || DEFAULT_LANGUAGE);
    setAssets((deck.assets && deck.assets.length) ? deck.assets : []);
    setDeckError(null);

    // Scroll to form on mobile
    if (deckFormRef.current && window.innerWidth < 768) {
      deckFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Cancel editing deck - reset form
  const handleCancelEditDeck = (e) => {
    e?.stopPropagation();
    setEditingDeck(null);
    setDeckName('');
    setDeckTopic('');
    setDeckIdentityKey('');
    setDeckIdentityId('');
    setDeckDurationFrom('');
    setDeckDurationTo('');
    setDeckPriority('');
    setDeckPosition('');
    setCoverType('none');
    setDeckLangCode('en');
    setAssets([]);
    setDeckError(null);
    setUploadingSlot(null);
    setUploadProgress(0);
    setDeckNameError('');
    setDeckTopicError('');
    setDeckDurationError('');
    setDeckPriorityError('');
    setDeckPositionError('');
    setAssetsError('');
  };

  // Start deleting deck (show confirmation)
  const handleStartDeleteDeck = (deck, e) => {
    e.stopPropagation();
    // Prevent deleting for archived or deleted decks
    if (deck.status === 'archived' || deck.status === 'deleted') {
      return;
    }
    // For draft status, archive; for created/published, delete
    if (deck.status === 'draft') {
      setArchivingDeckId(deck._id);
    } else {
      setDeletingDeckId(deck._id);
    }
  };

  // Confirm deck deletion/archiving
  const handleConfirmDeleteDeck = async (deckId, deck, e) => {
    e?.stopPropagation();
    const isArchive = deck.status === 'draft';
    try {
      const response = await deckService.archiveDeck(deckId);
      if (response.success) {
        setToast({ message: isArchive ? 'Deck archived successfully' : 'Deck deleted successfully', type: 'success' });
        // Refresh deck list
        setDeckPage(1);
        if (deckSearch.trim()) {
          const searchParams = { page: 1, limit: 5 };
          if (deckStatusFilter !== 'all') {
            searchParams.status = deckStatusFilter;
          }
          const searchResponse = await deckService.searchDecks(deckSearch.trim(), searchParams);
          if (searchResponse?.success) {
            setDecks(searchResponse.data.decks || []);
            setDeckTotalPages(searchResponse.data.totalPages || 1);
            setDeckHasMore(1 < (searchResponse.data.totalPages || 1));
          }
        } else {
          await fetchDecks(1, false);
        }
        // Clear selected deck if it was the deleted one
        if (selectedDeck === deckId) {
          setSelectedDeck(null);
        }
      } else {
        setToast({ message: response.message || (isArchive ? 'Failed to archive deck' : 'Failed to delete deck'), type: 'error' });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.message || (isArchive ? 'Failed to archive deck' : 'Failed to delete deck');
      setDeckError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setDeletingDeckId(null);
      setArchivingDeckId(null);
    }
  };

  // Cancel deck deletion/archiving
  const handleCancelDeleteDeck = (e) => {
    e?.stopPropagation();
    setDeletingDeckId(null);
    setArchivingDeckId(null);
  };

  return (
    <>
      <div className={`deck-page ${fullWidth ? 'deck-page--full-width' : ''}`}>
      <div className={`deck-page-layout deck-page-layout--${layout}`}>
        {/* Left Partition - Deck draft Form Only */}
        <div className="deck-create-form" ref={deckFormRef}>
          <div className="section-header">
            <span className="section-title"><span style={{ fontSize: '1.5em' }}>📘</span> {editingDeck ? 'UPDATE Deck' : 'DRAFT Deck'}</span>
          </div>

          {/* Create Deck Form */}
          <div className="deck-create-container">
            {/* Row 1: Deck Heading */}
            <input
              type="text"
              value={deckName}
              onChange={(e) => {
                setDeckName(e.target.value);
                setDeckNameError('');
                setDeckError(null);
              }}
              placeholder="Enter Name or Heading"
              className={`deck-form-input ${deckNameError ? 'deck-form-input--error' : ''}`}
            />
            {deckNameError && <div className="deck-field-error">{deckNameError}</div>}

            {/* Row 2: Category and Language */}
            <div className="deck-form-row-two-inputs">
              <div className="deck-dropdown-wrapper">
                <select
                  value={deckTopic}
                  onChange={(e) => {
                    setDeckTopic(e.target.value);
                    setDeckTopicError('');
                    setDeckError(null);
                    // Auto-set identityKey and identityId based on selected topic
                    const selectedTopic = topics.find(t => t.name === e.target.value);
                    if (selectedTopic) {
                      setDeckIdentityKey(selectedTopic.identityKey);
                      setDeckIdentityId(selectedTopic.id);
                    }
                    // Don't clear existing values if no match - they might be from a custom/legacy topic
                  }}
                  className={`deck-form-select ${deckTopicError ? 'deck-form-select--error' : ''}`}
                  disabled={topicLoading || (editingDeck && editingDeck.status !== 'draft')}
                >
                  <option value="">Select Topic</option>
                  {topics.map((topic) => (
                    <option key={topic.id} value={topic.name}>
                      {topic.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="dropdown-icon" />
              </div>

              <div className="deck-dropdown-wrapper">
                <select
                  value={deckLangCode}
                  onChange={(e) => {
                    setDeckLangCode(e.target.value);
                    setDeckError(null);
                  }}
                  className="deck-form-select"
                  disabled={languageLoading || (editingDeck && editingDeck.status !== 'draft')}
                >
                  {languages.filter(lang => lang.enabled).map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name} ({lang.code})
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="dropdown-icon" />
              </div>
            </div>
            {deckTopicError && <div className="deck-field-error">{deckTopicError}</div>}

            {/* Row 3: From and To Dates */}
            <div className={`deck-form-row-two-inputs deck-dates-row ${deckDurationError ? 'deck-form-row-two-inputs--error' : ''}`}>
              <input
                type="datetime-local"
                value={deckDurationFrom}
                onChange={(e) => {
                  setDeckDurationFrom(e.target.value);
                  setDeckDurationError('');
                  setDeckError(null);
                }}
                className={`deck-form-input deck-date-input ${deckDurationError ? 'deck-form-input--error' : ''}`}
              />
              <span className="date-separator-text">To</span>
              <input
                type="datetime-local"
                value={deckDurationTo}
                onChange={(e) => {
                  setDeckDurationTo(e.target.value);
                  setDeckDurationError('');
                  setDeckError(null);
                }}
                className={`deck-form-input deck-date-input ${deckDurationError ? 'deck-form-input--error' : ''}`}
              />
            </div>
            {deckDurationError && <div className="deck-field-error">{deckDurationError}</div>}

            {/* Row 4: Priority and Position */}
            <div className="deck-form-row-two-inputs">
              <input
                type="text"
                inputMode="numeric"
                value={deckPriority}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers
                  if (value === '' || /^\d+$/.test(value)) {
                    setDeckPriority(value);
                    setDeckPriorityError('');
                    setDeckError(null);
                  }
                }}
                placeholder="Enter Priority (1-1000)"
                className={`deck-form-input ${deckPriorityError ? 'deck-form-input--error' : ''}`}
              />
              {deckPriorityError && <div className="deck-field-error deck-field-error--inline">{deckPriorityError}</div>}
              <input
                type="text"
                inputMode="numeric"
                value={deckPosition}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers
                  if (value === '' || /^\d+$/.test(value)) {
                    setDeckPosition(value);
                    setDeckPositionError('');
                    setDeckError(null);
                  }
                }}
                placeholder="Enter Position (1-1000)"
                className={`deck-form-input ${deckPositionError ? 'deck-form-input--error' : ''}`}
              />
              {deckPositionError && <div className="deck-field-error deck-field-error--inline">{deckPositionError}</div>}
            </div>

            {/* Row 5: Cover Type */}
            <div className="deck-dropdown-wrapper">
              <select
                value={coverType}
                onChange={handleCoverTypeChange}
                className="deck-form-select"
              >
                <option value="none">Cover Type (None)</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>
              <ChevronDown size={14} className="dropdown-icon" />
            </div>

            {/* Assets Upload Section */}
            {coverType !== 'none' && (
              <div className="deck-assets-section">
                {coverType === 'image' ? (
                  // Image upload - 3 fixed slots, one by one
                  <>
                    <input
                      type="file"
                      ref={coverInputRef}
                      onChange={handleAssetUpload}
                      accept="image/*"
                      multiple={false}
                      style={{ display: 'none' }}
                    />
                    <div className="deck-image-slots-grid">
                      {[0, 1, 2].map((slotIndex) => {
                        const asset = assets[slotIndex];
                        const isSlotFilled = !!asset;
                        // Check if previous slots are filled (this slot is clickable if all previous slots are filled or this is slot 0)
                        const hasEmptyBefore = assets.slice(0, slotIndex).some(a => a === null || a === undefined);
                        const isClickable = !hasEmptyBefore;

                        return (
                          <div key={slotIndex} className="deck-image-slot-wrapper">
                            {/* Image Preview Slot */}
                            {uploadingSlot === slotIndex ? (
                              <div className="deck-image-slot deck-image-slot--uploading">
                                <div className="deck-upload-spinner"></div>
                                <span className="deck-upload-progress">{uploadProgress}%</span>
                                <span className="deck-upload-text">Uploading...</span>
                              </div>
                            ) : isSlotFilled ? (
                              <div className="deck-image-slot deck-image-slot--filled">
                                <img src={asset.source_url} alt={`Image ${slotIndex + 1}`} />
                                <button
                                  type="button"
                                  className="deck-image-slot-remove"
                                  onClick={() => handleRemoveAsset(slotIndex)}
                                >
                                  <X size={14} />
                                </button>
                                <span className="deck-image-slot-number">{slotIndex + 1}</span>
                              </div>
                            ) : (
                              <div
                                className={`deck-image-slot deck-image-slot--empty ${!isClickable ? 'deck-image-slot--disabled' : ''}`}
                                onClick={() => {
                                  if (isClickable) {
                                    coverInputRef.current?.click();
                                  }
                                }}
                              >
                                <ImageIcon size={24} />
                                <Upload size={14} />
                                <span>Upload Image {slotIndex + 1}</span>
                              </div>
                            )}

                            {/* Text Input below each image */}
                            <input
                              type="text"
                              value={isSlotFilled ? asset.text : ''}
                              onChange={(e) => handleAssetTextChange(slotIndex, e.target.value)}
                              placeholder={`Text for Image ${slotIndex + 1}`}
                              className="deck-form-input deck-image-slot-text"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  // Video/Audio upload - single file
                  <>
                    <input
                      type="file"
                      ref={coverInputRef}
                      onChange={handleAssetUpload}
                      accept={coverType === 'video' ? 'video/*' : 'audio/*'}
                      multiple={false}
                      style={{ display: 'none' }}
                    />
                    {uploadingSlot === 'media' ? (
                      <div className="deck-upload-area deck-upload-area--uploading">
                        <div className="deck-upload-spinner deck-upload-spinner--large"></div>
                        <span className="deck-upload-progress">{uploadProgress}%</span>
                        <span className="deck-upload-text">Uploading...</span>
                      </div>
                    ) : assets.length === 0 ? (
                      <div
                        className="deck-upload-area"
                        onClick={() => coverInputRef.current?.click()}
                      >
                        <div className="deck-upload-icon">
                          {coverType === 'video' && <Video size={32} />}
                          {coverType === 'audio' && <Music size={32} />}
                          <Upload size={16} />
                        </div>
                        <span>
                          {coverType === 'video' ? 'Click to upload video' :
                          'Click to upload audio'}
                        </span>
                      </div>
                    ) : (
                      <div className="deck-assets-list">
                        {assets.map((asset, index) => (
                          <div key={index} className="deck-asset-item">
                            {asset.type === 'VIDEO' && (
                              <div className="deck-asset-preview deck-asset-preview-video">
                                <Video size={24} />
                              </div>
                            )}
                            {asset.type === 'AUDIO' && (
                              <div className="deck-asset-preview deck-asset-preview-audio">
                                <Music size={24} />
                              </div>
                            )}
                            <div className="deck-asset-text-input">
                              <input
                                type="text"
                                value={asset.text}
                                onChange={(e) => handleAssetTextChange(index, e.target.value)}
                                placeholder="Enter text for this asset"
                                className="deck-form-input"
                              />
                            </div>
                            <button
                              type="button"
                              className="deck-asset-remove"
                              onClick={() => handleRemoveAsset(index)}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {assetsError && <div className="deck-field-error">{assetsError}</div>}

            {/* Draft/Update Deck Buttons */}
            <div className="deck-form-actions">
              {editingDeck && (
                <button
                  className="cancel-deck-button"
                  onClick={handleCancelEditDeck}
                  disabled={creatingDeck}
                >
                  Cancel
                </button>
              )}
              <button
                className={`create-deck-button ${editingDeck ? 'create-deck-button--update' : ''}`}
                onClick={editingDeck ? handleUpdateDeck : handleCreateDeck}
                disabled={creatingDeck || !deckName.trim()}
              >
                {creatingDeck ? (editingDeck ? 'Updating...' : 'Drafting...') : (editingDeck ? 'Update Deck' : 'Draft Deck')}
              </button>
            </div>
          </div>

          {deckError && (
            <div className="deck-error-message" style={{ marginBottom: '1rem' }}>
              {deckError}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="partition-divider"></div>

        {/* Right Partition - Deck List Section */}
        <div className="deck-list-form">
          <div className="section-header">
            <span className="section-title"><span style={{ fontSize: '1.5em' }}>📚</span> Decks List</span>
            <div className="search-filter-wrapper">
              {/* Status Filter Dropdown */}
              <Dropdown
                options={statusFilterOptions}
                selectedValue={deckStatusFilter}
                onSelect={setDeckStatusFilter}
                type="Status"
                className="status-filter-dropdown"
              />
              <div className="search-filter-divider"></div>
              {/* Deck Search */}
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search deck..."
                  value={deckSearch}
                  onChange={(e) => setDeckSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Deck List */}
          <div className="deck-list" ref={deckListRef}>
            {deckLoading ? (
              <div className="deck-empty-state">
                <Loader />
              </div>
            ) : deckSearchLoading ? (
              <div className="deck-empty-state">
                <Loader />
              </div>
            ) : (!decks || decks.length === 0) ? (
              <div className="deck-empty-state">
                <div className="deck-empty-icon">📚</div>
                <p>{deckSearch.trim() ? 'No decks found' : 'No decks to show'}</p>
              </div>
            ) : (
              <div className="deck-items-list">
                {decks.map((deck) => {
                  const getUserName = (updatedBy) => {
                    if (!updatedBy) return 'Unknown';
                    if (updatedBy.firstName || updatedBy.lastName) {
                      return `${updatedBy.firstName || ''} ${updatedBy.lastName || ''}`.trim();
                    }
                    return updatedBy.email?.split('@')[0] || 'Unknown';
                  };

                  const formatUpdatedTime = (dateString) => {
                    if (!dateString) return '';
                    const date = new Date(dateString);
                    const now = new Date();
                    const diffMs = now - date;
                    const diffMins = Math.floor(diffMs / 60000);
                    const diffHours = Math.floor(diffMs / 3600000);
                    const diffDays = Math.floor(diffMs / 86400000);
                    const diffMonths = Math.floor(diffDays / 30);
                    const diffYears = Math.floor(diffDays / 365);

                    if (diffMins < 1) return 'Just now';
                    if (diffMins < 60) return `${diffMins}m ago`;
                    if (diffHours < 24) return `${diffHours}h ago`;
                    if (diffDays < 30) return `${diffDays}d ago`;
                    if (diffMonths < 12) return `${diffMonths}mo ago`;
                    return `${diffYears}y ago`;
                  };

                  const formatDateTime = (dateString) => {
                    if (!dateString) return '';
                    const date = new Date(dateString);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = String(date.getFullYear()).slice(-2);
                    let hours = date.getHours();
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    hours = hours % 12;
                    hours = hours || 12;
                    return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
                  };

                  return (
                    <div
                      key={deck._id}
                      className={`deck-card deck-card--${deck.status?.toLowerCase() || 'draft'} ${selectedDeck === deck._id ? 'deck-card--selected' : ''} ${editingDeck?._id === deck._id ? 'deck-card--editing' : ''}`}
                      onClick={(e) => handleDeckClick(e, deck._id, deck.status)}
                    >
                      {/* Top Section - Thin Header */}
                      <div className="deck-card-header">
                        <div className="deck-card-header-left">
                          <span className="deck-item-icon">📘</span>
                          <span className="deck-item-name">{deck.name}</span>
                        </div>
                        <div className="deck-card-header-right">
                          <span className={`deck-status-badge deck-status-badge--${deck.status}`}>
                            {deck.status ? deck.status.toUpperCase() : 'DRAFT'}
                          </span>
                        </div>
                      </div>

                      {/* Bottom Section - Thick Body */}
                      <div className="deck-card-body">
                        {/* Left Media Section - Always shown */}
                        <DeckCardMedia assets={deck.assets || []} coverType={deck.coverType} />

                        <div className="deck-card-content">
                              <div className="deck-card-row">
                                <span className="deck-topic-badge">{deck.topic || '---'}</span>
                                <span className="deck-meta-text">
                                  <FileText size={11} />
                                  {deck.newsCount || 0} news
                                </span>
                                <span className="deck-meta-text">
                                  <Clock size={11} />
                                  {formatUpdatedTime(deck.createdAt)}
                                </span>
                              </div>
                              <div className="deck-card-row deck-dates-row">
                                  <Calendar size={10} />
                                <div className="deck-date-item">
                                  <span>{deck.durationFrom ? formatDateTime(deck.durationFrom) : '---'}</span>
                                  <span> - </span>
                                  <span>{deck.durationTo ? formatDateTime(deck.durationTo) : '---'}</span>
                                </div>
                              </div>
                              <div className="deck-card-row">
                                <span className="deck-creator-text">
                                  <UserIcon size={11} />
                                  Created By: {deck.createdBy?.email}
                                </span>
                              </div>
                            </div>
                            <div className="deck-card-actions">
                              {deck.status !== 'archived' && deck.status !== 'deleted' && (
                                <div className="deck-actions-vertical">
                                  <div className="deck-action-wrapper">
                                    <button
                                      className="deck-action-btn-vertical deck-action-edit"
                                      onClick={(e) => handleStartEditDeck(deck, e)}
                                    >
                                      <Pencil size={12} />
                                    </button>
                                    <span className="deck-action-tooltip">edit</span>
                                  </div>
                                  {deck.status === 'draft' ? (
                                    <div className="deck-action-wrapper">
                                      <button
                                        className="deck-action-btn-vertical deck-action-archive"
                                        onClick={(e) => handleStartDeleteDeck(deck, e)}
                                      >
                                        <ArchiveRestore size={12} />
                                      </button>
                                      <span className="deck-action-tooltip">archive</span>
                                    </div>
                                  ) : (
                                    <div className="deck-action-wrapper">
                                      <button
                                        className="deck-action-btn-vertical deck-action-delete"
                                        onClick={(e) => handleStartDeleteDeck(deck, e)}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                      <span className="deck-action-tooltip">delete</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Show edit and delete icons for archived/deleted decks */}
                              {(deck.status === 'archived' || deck.status === 'deleted') && (
                                <div className="deck-actions-vertical deck-actions-vertical--disabled">
                                  <button
                                    className="deck-action-btn-vertical deck-action-edit deck-action-btn-vertical--disabled"
                                    style={{ cursor: 'not-allowed' }}
                                    onClick={(e) => { e.stopPropagation(); }}
                                    title="View details"
                                  >
                                    <Pencil size={12} />
                                  </button>
                                  <button
                                    className="deck-action-btn-vertical deck-action-delete deck-action-btn-vertical--disabled"
                                    style={{ cursor: 'not-allowed' }}
                                    onClick={(e) => { e.stopPropagation(); }}
                                    title="Deleted"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Confirmation Overlay */}
                            {(deletingDeckId === deck._id || archivingDeckId === deck._id) && (
                              <div className="deck-card-confirm-overlay" onClick={(e) => e.stopPropagation()}>
                                <span className="deck-card-confirm-text">
                                  {deck.status === 'draft' ? 'Sure? Archive this Deck?' : 'Sure? Delete this Deck?'}
                                </span>
                                <div className="deck-card-confirm-buttons">
                                  <button
                                    className="deck-card-confirm-yes"
                                    onClick={(e) => handleConfirmDeleteDeck(deck._id, deck, e)}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    className="deck-card-confirm-no"
                                    onClick={(e) => handleCancelDeleteDeck(e)}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Mobile actions - horizontal partition at bottom (outside body) */}
                          <div className="deck-card-actions-mobile">
                            <button
                              className={`deck-action-btn-mobile deck-action-edit-mobile ${(deck.status === 'archived' || deck.status === 'deleted') ? 'deck-action-btn-mobile--disabled' : ''}`}
                              onClick={(e) => handleStartEditDeck(deck, e)}
                              disabled={deck.status === 'archived' || deck.status === 'deleted'}
                            >
                              <Pencil size={16} />
                              Edit
                            </button>
                            <button
                              className={`deck-action-btn-mobile ${deck.status === 'draft' ? 'deck-action-archive-mobile' : 'deck-action-delete-mobile'} ${(deck.status === 'archived' || deck.status === 'deleted') ? 'deck-action-btn-mobile--disabled' : ''}`}
                              onClick={(e) => handleStartDeleteDeck(deck, e)}
                              disabled={deck.status === 'archived' || deck.status === 'deleted'}
                            >
                              {deck.status === 'draft' ? <ArchiveRestore size={16} /> : <Trash2 size={16} />}
                              {deck.status === 'draft' ? 'Archive' : 'Delete'}
                            </button>
                          </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Loading indicator for pagination */}
            {(deckLoading && deckPage > 1) && (
              <div className="deck-pagination-loading">
                <Loader size="small" />
                <span>Loading more decks...</span>
              </div>
            )}

            {/* Load More button/link */}
            {deckHasMore && !deckLoading && (
              <div className="deck-load-more" onClick={() => fetchDecks(deckPage + 1, true)}>
                <span>Load More ({deckPage + 1}/{deckTotalPages})</span>
              </div>
            )}
          </div>
        </div>
      </div>
        </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
export default DeckManager;
